import { isPosthogFlagOn, PosthogFeatureFlags, posthog } from '@/lib/posthog'
import { usePwaStore } from '@/store/pwa'
import type { BeforeInstallPromptEvent } from '@/types/beforeInstallPromptEvent'

import { startAppVersionWatch } from './appVersionWatch'
import { registerPwaInstallAnalytics } from './pwaAnalytics'
import { registerLaunchHandler } from './registerLaunchHandler'
import { registerServiceWorkerDynamic } from './registerServiceWorker'
import { unregisterServiceWorkers } from './unregisterServiceWorker'

let pwaClientInitialized = false
let serviceWorkerLifecycleStarted = false

/**
 * The discretionary PWA experience — install prompt (Chromium) and link launch
 * handling. Gated on the PostHog `pwa` flag, which is what that flag is for.
 */
export function initPwaClient() {
  if (pwaClientInitialized) return
  pwaClientInitialized = true

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    usePwaStore().setDeferredInstallPrompt(event as BeforeInstallPromptEvent)
  })

  registerLaunchHandler()
}

/**
 * Service worker lifecycle — deliberately NOT skipped when the `pwa` flag is off.
 *
 * A worker already installed on a user's machine keeps serving its precached shell
 * whatever the flag says; the flag only decides whether this app still talks to it.
 * So the off branch has to actively unregister, or the flag silently strands those
 * users on the build they had when it was flipped. Production only — no worker is
 * ever registered in dev (`VitePWA({ disable })`).
 */
function startServiceWorkerLifecycle(isPwaEnabled: boolean) {
  if (!import.meta.env.PROD || serviceWorkerLifecycleStarted) return
  serviceWorkerLifecycleStarted = true

  if (isPwaEnabled) registerServiceWorkerDynamic()
  else void unregisterServiceWorkers()
}

/** Wait for PostHog feature flags before starting the flag-gated PWA install/launch UX. */
export function schedulePwaClientInit() {
  registerPwaInstallAnalytics()

  // Independent of every flag and of the service worker: a tab running a superseded
  // build is a problem for all users, and the most common cause of it — a cached
  // `index.html` — happens above the layer a service worker can reach.
  startAppVersionWatch()

  const tryInit = () => {
    const isPwaEnabled = isPosthogFlagOn(PosthogFeatureFlags.Pwa)
    startServiceWorkerLifecycle(isPwaEnabled)
    if (isPwaEnabled) initPwaClient()
  }

  if (!import.meta.env.VITE_POSTHOG_API_KEY) {
    tryInit()
    return
  }

  posthog.onFeatureFlags(tryInit)
}
